import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import { Collapse } from "@talentbait/shared-components";

import { Header } from "../text-edition/TextEdition.styled";
import { ActionsAdAssistant } from "../../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import AdAssistantStore from "../../AdAssistantStore";
import { withRefluxStore } from "../../../../Stores";
import { FlexWrapper, Loader } from "../../AdAssistant.styled";
import {
  CollapseContainerStyle,
  CollapseDivider,
  CollapseDividerVertical,
  CollapseHeader,
  Dropdown,
  HideLogoCheck,
  LogoImageContainer,
  LogoPositionButton,
  LogoSizeDivider,
  LogoSizeSelector,
  LogoSizeText,
  LogoSizeButton,
  SectionHeader,
  NoLogosWarning
} from "./LogoEdition.styled";
import {
  BottomLeftIcon,
  BottomRightIcon,
  TopLeftIcon,
  TopRightIcon
} from "../../../../Components/Icons";
import Actions from "../../../../Actions";
import { ChevronDown } from "@untitledui/icons/ChevronDown";
import { ChevronLeft } from "@untitledui/icons/ChevronLeft";
import { ChevronRight } from "@untitledui/icons/ChevronRight";
import { AssetSlider } from "../../../../Components/AssetSlider/AssetSlider";
import UploadInput from "../image-edition/upload-input/UploadInput";

// Define logo size optionss
const LOGO_SIZE_OPTIONS = [
  { value: "small", label: "Small" },
  { value: "mid", label: "Medium" },
  { value: "big", label: "Big" }
];

const LogoEdition = ({
  saveNewInputProps,
  mediaAssets,
  orientationSelected,
  // Store
  uploadedLogos,
  campaignData,
  organization
}) => {
  const { t } = useTranslation();
  const { companyId } = campaignData;

  const orientation = orientationSelected.value === "all" ? "vertical" : orientationSelected.value;

  const currentLogo = useMemo(
    () => mediaAssets[orientation].inputProps.logo,
    [mediaAssets, orientation]
  );
  const currentLogoSize = useMemo(
    () => mediaAssets[orientation].inputProps.logoSize,
    [mediaAssets, orientation]
  );

  const currentLogoPosition = useMemo(
    () => mediaAssets[orientation].inputProps.logoPosition,
    [mediaAssets, orientation]
  );

  const loadedLogoSize = LOGO_SIZE_OPTIONS.find((option) => option.value === currentLogoSize);

  const initialValues = {
    logoUrl: currentLogo,
    logoPosition: currentLogoPosition,
    hideLogoSelected: !currentLogo,
    logoSize: loadedLogoSize
  };

  // Define options for the dropdown
  const logoPositionOptions = [
    { value: "top-left", icon: <TopLeftIcon isSelected={currentLogoPosition === "top-left"} /> },
    { value: "top-right", icon: <TopRightIcon isSelected={currentLogoPosition === "top-right"} /> },
    {
      value: "bottom-left",
      icon: <BottomLeftIcon isSelected={currentLogoPosition === "bottom-left"} />
    },
    {
      value: "bottom-right",
      icon: <BottomRightIcon isSelected={currentLogoPosition === "bottom-right"} />
    }
  ];

  const [brandKitLogos, setBrandKitLogos] = useState({});
  const [hideLogoSelected, setHideLogoSelected] = useState(initialValues.hideLogoSelected);
  const [logoPosition, setLogoPosition] = useState(initialValues.logoPosition);
  const [logoUrl, setLogoUrl] = useState(initialValues.logoUrl);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedLogoSize, setSelectedLogoSize] = useState(initialValues.logoSize);

  const [showLogoSettings, setShowLogoSettings] = useState(false);
  const [showLogo, setShowLogo] = useState(true);

  const [logoDimensions, setLogoDimensions] = useState({});

  useEffect(() => {
    if (!uploadedLogos) return;
    setIsLoadingImage(false);
  }, [uploadedLogos]);

  const allLogos = useMemo(() => {
    const validBrandKitLogos = Object.values(brandKitLogos).filter(
      (logo) => logo !== null && logo !== ""
    );
    return [...uploadedLogos, ...validBrandKitLogos];
  }, [uploadedLogos, brandKitLogos]);

  const isLogoLandscape = useCallback(
    (logoUrl) => {
      if (logoDimensions[logoUrl]) {
        return logoDimensions[logoUrl].width > logoDimensions[logoUrl].height;
      }
      return false;
    },
    [logoDimensions]
  );

  useEffect(() => {
    allLogos.forEach((logoUrl) => {
      if (!logoDimensions[logoUrl]) {
        const img = new Image();
        img.onload = () => {
          setLogoDimensions((prev) => ({
            ...prev,
            [logoUrl]: {
              width: img.width,
              height: img.height
            }
          }));
        };
        img.src = logoUrl;
      }
    });
  }, [allLogos, logoDimensions]);

  const squareLogos = useMemo(() => {
    return allLogos.filter((logo) => !isLogoLandscape(logo));
  }, [allLogos, isLogoLandscape]);

  const landscapeLogos = useMemo(() => {
    return allLogos.filter((logo) => isLogoLandscape(logo));
  }, [allLogos, isLogoLandscape]);

  useEffect(() => {
    setLogoUrl(initialValues.logoUrl);
    setLogoPosition(initialValues.logoPosition);
    setHideLogoSelected(initialValues.hideLogoSelected);
    setSelectedLogoSize(initialValues.logoSize);
  }, [
    initialValues.logoUrl,
    initialValues.logoPosition,
    initialValues.hideLogoSelected,
    initialValues.logoSize
  ]);

  useEffect(() => {
    ActionsAdAssistant.getCloudinaryImagesByCompanyId(companyId);
    Actions.getBrandKitByCompanyId(companyId);
    const brandkitSuccessfully = Actions.getBrandKitByCompanyId.completed.listen((result) => {
      const { payload } = result;
      setBrandKitLogos(payload.logos);
      brandkitSuccessfully();
    });
  }, [companyId]);

  // This is not really a save, it's just a notification for the user
  // as we save the changes directly in every change action
  const handleSaveChanges = () => {
    Actions.addNotification(t("Saved correctly"));
  };

  const handleLogoSelection = (logoUrl) => {
    setLogoUrl(logoUrl);
    setHideLogoSelected(false);

    // Directly save the changes
    saveNewInputProps([{ key: "logo", value: logoUrl }], true);
  };

  // Add a new handler for position changes
  const handlePositionChange = (newPosition) => {
    setLogoPosition(newPosition);

    // Directly save the changes
    saveNewInputProps([{ key: "logoPosition", value: newPosition }], true);
  };

  const handleLogoSizeChange = (option) => {
    setSelectedLogoSize(option);

    // Directly save the changes
    saveNewInputProps([{ key: "logoSize", value: option.value }], true);
  };

  const handleHideLogoSelection = (event) => {
    const isHidingLogo = event.target.checked;
    setHideLogoSelected(isHidingLogo);

    // Directly save the changes
    saveNewInputProps([{ key: "logo", value: isHidingLogo ? "" : logoUrl }], true);
  };

  const onFileChange = (photoData) => {
    setIsLoadingImage(true);
    handleLogoSelection(photoData);

    setTimeout(() => {
      ActionsAdAssistant.getCloudinaryImagesByCompanyId(companyId);
      setIsLoadingImage(false);
    }, 4000);
  };

  const showLoading = isLoadingImage || isUploading;

  return (
    <FlexWrapper direction={"column"} align={"flex-start"} gap={10} width={"100%"}>
      <Collapse
        parent={
          <CollapseHeader
            direction={"row"}
            width={"100%"}
            justify={"space-between"}
            onClick={() => setShowLogo(!showLogo)}
          >
            {t("Logo")}
            <Dropdown $isOpen={showLogo} onClick={() => setShowLogo(!showLogo)}>
              <ChevronDown color="var(--primary-dark-300)" />
            </Dropdown>
          </CollapseHeader>
        }
        show={showLogo}
        styles={CollapseContainerStyle}
      >
        <CollapseDivider />
        <AnimatePresence>
          <motion.div
            key={companyId}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              marginTop: "10px",
              padding: "10px 20px"
            }}
          >
            <HideLogoCheck htmlFor="logo">
              <input
                type="checkbox"
                name="logo"
                id="logo"
                value={hideLogoSelected}
                checked={hideLogoSelected}
                onChange={handleHideLogoSelection}
              />
              {t("Hide logo")}
            </HideLogoCheck>

            <LogoSizeDivider />

            <FlexWrapper gap={15} align={"flex-end"} wrap={"wrap"} justify={"flex-start"}>
              <SectionHeader>{t("Square Logo")}</SectionHeader>
              <AssetSlider
                show={true}
                sliderHeight={squareLogos.length > 0 ? 100 : 50}
                assetsGap={10}
              >
                {showLoading ? (
                  <FlexWrapper
                    width={"100%"}
                    height={"100%"}
                    justify={"center"}
                    align={"flex-start"}
                  >
                    <Loader />
                  </FlexWrapper>
                ) : squareLogos.length === 0 && !showLoading ? (
                  <NoLogosWarning>{t("No square logos found")}</NoLogosWarning>
                ) : (
                  squareLogos.map((logo) => {
                    const isSelected = logo === logoUrl;

                    return (
                      <LogoImageContainer
                        key={logo}
                        onClick={() => handleLogoSelection(logo)}
                        style={{ backgroundImage: `url(${logo})` }}
                        isLandscape={false}
                        isSelected={isSelected}
                      />
                    );
                  })
                )}
              </AssetSlider>

              <LogoSizeDivider />

              <SectionHeader>{t("Word and design logo")}</SectionHeader>
              <AssetSlider
                show={true}
                sliderHeight={landscapeLogos.length > 0 ? 100 : 50}
                assetsGap={10}
              >
                {showLoading ? (
                  <FlexWrapper
                    width={"100%"}
                    height={"100%"}
                    justify={"center"}
                    align={"flex-start"}
                  >
                    <Loader />
                  </FlexWrapper>
                ) : landscapeLogos.length === 0 && !showLoading ? (
                  <NoLogosWarning>{t("No word and design logos found")}</NoLogosWarning>
                ) : (
                  landscapeLogos.map((logo) => {
                    const isSelected = logo === logoUrl;
                    return (
                      <LogoImageContainer
                        key={logo}
                        onClick={() => handleLogoSelection(logo)}
                        style={{ backgroundImage: `url(${logo})` }}
                        isLandscape={true}
                        isSelected={isSelected}
                      />
                    );
                  })
                )}
              </AssetSlider>

              <LogoSizeDivider />

              <FlexWrapper width={"100%"} direction={"column"} align={"flex-start"} gap={10}>
                <SectionHeader>{t("Upload new logo")}</SectionHeader>
                <UploadInput
                  emitNewImageUpload={onFileChange}
                  orgaId={organization?.id}
                  companyId={companyId}
                  isLogo={true}
                  emitIsUploading={setIsUploading}
                />
              </FlexWrapper>
            </FlexWrapper>
          </motion.div>
        </AnimatePresence>
      </Collapse>

      {/* Logo settings */}
      <Collapse
        parent={
          <CollapseHeader
            direction={"row"}
            width={"100%"}
            justify={"space-between"}
            onClick={() => setShowLogoSettings(!showLogoSettings)}
          >
            {t("Settings")}
            <Dropdown
              $isOpen={showLogoSettings}
              onClick={() => setShowLogoSettings(!showLogoSettings)}
            >
              <ChevronDown color="var(--primary-dark-300)" />
            </Dropdown>
          </CollapseHeader>
        }
        show={showLogoSettings}
        styles={CollapseContainerStyle}
      >
        <CollapseDivider />
        <AnimatePresence>
          <motion.div
            key={isLoadingImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              marginTop: "10px",
              padding: "10px 20px"
            }}
          >
            <FlexWrapper
              gap={15}
              width={"100%"}
              justify={"flex-start"}
              style={{ minHeight: "68px", height: "68px" }}
            >
              <FlexWrapper
                gap={15}
                direction={"column"}
                align={"flex-start"}
                justify={"flex-start"}
              >
                <section>
                  <Header>{t("Position")}</Header>
                </section>
                <FlexWrapper gap={5} direction={"row"} align={"center"} justify={"flex-start"}>
                  {logoPositionOptions.map((option) => (
                    <LogoPositionButton
                      key={option.value}
                      onClick={() => handlePositionChange(option.value)}
                      isSelected={logoPosition === option.value}
                    >
                      {option.icon}
                    </LogoPositionButton>
                  ))}
                </FlexWrapper>
              </FlexWrapper>

              <CollapseDividerVertical />

              <FlexWrapper gap={15} direction={"column"} align={"flex-start"}>
                <section>
                  <Header>{t("Size")}</Header>
                </section>
                <LogoSizeSelector>
                  <LogoSizeButton
                    onClick={() => {
                      const currentIndex = LOGO_SIZE_OPTIONS.findIndex(
                        (opt) => opt.value === selectedLogoSize.value
                      );
                      const prevIndex =
                        currentIndex > 0 ? currentIndex - 1 : LOGO_SIZE_OPTIONS.length - 1;
                      handleLogoSizeChange(LOGO_SIZE_OPTIONS[prevIndex]);
                    }}
                  >
                    <ChevronLeft color="var(--primary-dark-300)" size={16} />
                  </LogoSizeButton>
                  <LogoSizeText>{selectedLogoSize.label}</LogoSizeText>
                  <LogoSizeButton
                    onClick={() => {
                      const currentIndex = LOGO_SIZE_OPTIONS.findIndex(
                        (opt) => opt.value === selectedLogoSize.value
                      );
                      const nextIndex =
                        currentIndex < LOGO_SIZE_OPTIONS.length - 1 ? currentIndex + 1 : 0;
                      handleLogoSizeChange(LOGO_SIZE_OPTIONS[nextIndex]);
                    }}
                  >
                    <ChevronRight color="var(--primary-dark-300)" size={16} />
                  </LogoSizeButton>
                </LogoSizeSelector>
              </FlexWrapper>
            </FlexWrapper>
          </motion.div>
        </AnimatePresence>
      </Collapse>
    </FlexWrapper>
  );
};

export default withRefluxStore(
  LogoEdition,
  [AdAssistantStore],
  ["uploadedLogos", "campaignData", "organization"]
);
